import { ReactNode, useEffect, useState } from 'react';
import { Spinner } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import { ChatMessageItem, FesConfig, FesError, JobReport, JobTaskEvent, getChatMessages, getJobReport } from '../../fes';
import { ChatMessage, buildMessages } from '../chat/transcript';

// The largest Azure DevOps transcript on dev held 484 frames; the default 500 would cut longer runs.
const TRANSCRIPT_LIMIT = 2000;
const PROTOCOL_TAIL_LENGTH = 4000;

type StepCommand = { command: string; exitCode?: number };

/** Codex wraps every command in `/bin/sh -lc '…'`; the wrapper only takes up the row. */
export function displayCommand(command: string): string {
  const match = /^\/bin\/(?:ba)?sh -lc (['"])([\s\S]*)\1$/.exec(command.trim());
  return match ? match[2] : command;
}

/** Command lines and exit codes are only in the raw Codex events the transcript keeps as telemetry. */
export function readCommands(items: ChatMessageItem[]): Map<string, StepCommand> {
  const commands = new Map<string, StepCommand>();

  items.forEach(item => {
    const payload = item.payload || {};

    if (payload.type !== 'telemetry' || typeof payload.message !== 'string') {
      return;
    }

    try {
      const event = JSON.parse(payload.message) as {
        item?: { id?: string; type?: string; command?: unknown; exit_code?: unknown };
      };
      const codexItem = event.item;

      if (codexItem?.type !== 'command_execution' || !codexItem.id || typeof codexItem.command !== 'string') {
        return;
      }

      commands.set(codexItem.id, {
        command: codexItem.command,
        exitCode: typeof codexItem.exit_code === 'number' ? codexItem.exit_code : commands.get(codexItem.id)?.exitCode,
      });
    } catch {
      // Not every stdout line is JSON.
    }
  });

  return commands;
}

type ProtocolStep = { exitCode?: number; duration?: number; stdout?: string; stderr?: string };

/** rawPayload is base64 JSON of the run's steps; the tail of stdout is the only trace of a run that failed early. */
export function decodeProtocolPayload(rawPayload: string | undefined): ProtocolStep[] {
  if (!rawPayload) {
    return [];
  }

  try {
    // atob yields one character per byte; percent-decoding turns the bytes back into UTF-8 text.
    const percentEncoded = Array.from(atob(rawPayload), character => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`);
    const parsed = JSON.parse(decodeURIComponent(percentEncoded.join(''))) as { steps?: ProtocolStep[] };

    return Array.isArray(parsed.steps) ? parsed.steps : [];
  } catch {
    return [];
  }
}

function describeError(error: unknown): string {
  if (error instanceof FesError && (error.status === 403 || error.status === 404)) {
    return 'Not available for this job.';
  }

  return error instanceof Error ? error.message : 'Request failed.';
}

function useLazyLoad<T>(isOpen: boolean, load: () => Promise<T>) {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean; started: boolean }>({
    loading: false,
    started: false,
  });

  useEffect(() => {
    if (!isOpen || state.started) {
      return;
    }

    let cancelled = false;
    setState({ loading: true, started: true });
    load().then(
      data => !cancelled && setState({ data, loading: false, started: true }),
      error => !cancelled && setState({ error: describeError(error), loading: false, started: true })
    );

    return () => {
      cancelled = true;
    };
    // Loads once per opened job; the parent remounts this component for another job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return state;
}

function Section({
  title,
  meta,
  defaultOpen,
  children,
}: {
  title: string;
  meta?: string;
  defaultOpen: boolean;
  children: (isOpen: boolean) => ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <details
      className="app-skill-array-detail app-job-section"
      open={isOpen}
      onToggle={event => setIsOpen((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="app-skill-array-summary">
        <span className="app-skill-array-summary-title">{title}</span>
        {meta ? <span className="app-skill-array-summary-meta">{meta}</span> : null}
      </summary>
      <div className="app-skill-array-detail-body">{children(isOpen)}</div>
    </details>
  );
}

function Loading() {
  return (
    <Spinner center small>
      Loading...
    </Spinner>
  );
}

function StepRow({
  status,
  label,
  command,
  exitCode,
  output,
}: {
  status: string;
  label: string;
  command?: string;
  exitCode?: number;
  output: string;
}) {
  const failed = status === 'error' || (exitCode !== undefined && exitCode !== 0);

  return (
    <details className={`app-job-step${failed ? ' app-job-step--failed' : ''}`}>
      <summary>
        <span className="app-job-step-status">{failed ? 'error' : status}</span>
        <code className="app-job-step-label">{label}</code>
        {exitCode !== undefined ? <span className="app-job-step-meta">exit {exitCode}</span> : null}
      </summary>
      {command ? <code className="app-job-step-command">{command}</code> : null}
      {output ? <pre className="app-chat-tool-output">{output}</pre> : <p className="app-job-empty">No output.</p>}
    </details>
  );
}

function RunSteps({ messages, commands }: { messages: ChatMessage[]; commands: Map<string, StepCommand> }) {
  if (!messages.length) {
    return <p className="app-job-empty">No steps recorded. A run that fails before the agent starts leaves none.</p>;
  }

  return (
    <div className="app-job-steps">
      {messages.map(message => (
        <div className="app-job-steps-turn" key={message.id}>
          {(message.tools || []).map(tool => {
            const command = commands.get(tool.itemId);
            const shown = command ? displayCommand(command.command) : undefined;

            return (
              <StepRow
                key={tool.itemId}
                status={tool.status}
                label={shown || tool.tool}
                command={shown}
                exitCode={command?.exitCode}
                output={[tool.preview, tool.output].filter(Boolean).join('\n')}
              />
            );
          })}
          {message.content ? (
            <div className="app-job-agent-message">
              <span className="app-job-step-meta">{message.role === 'user' ? 'User' : 'Agent'}</span>
              <p>{message.content}</p>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function CallbackRow({ event }: { event: JobTaskEvent }) {
  const callback = event.callback;
  const delivered = callback?.httpStatus !== undefined && callback.httpStatus >= 200 && callback.httpStatus < 300;
  const outcome = callback?.httpStatus !== undefined ? String(callback.httpStatus) : callback?.error || 'no response';

  return (
    <details className={`app-job-step${delivered ? '' : ' app-job-step--failed'}`}>
      <summary>
        <span className="app-job-step-status">{outcome}</span>
        <code className="app-job-step-label">{event.lastStatus}</code>
        <span className="app-job-step-meta">
          attempt {callback?.attempt} · {callback?.durationMs} ms · {event.timestamp}
        </span>
      </summary>
      {callback?.body ? <pre className="app-chat-tool-output">{callback.body}</pre> : <p className="app-job-empty">Empty response.</p>}
    </details>
  );
}

function ReportBody({ report }: { report: JobReport }) {
  const events = report.taskEvents || [];
  const failures = events.filter(event => event.errorMessage);
  const callbacks = events.filter(event => event.kind === 'callback');
  const statuses = events.filter(event => event.kind !== 'callback');
  const protocolSteps = decodeProtocolPayload(report.agentProtocol?.rawPayload);

  return (
    <div className="app-job-report">
      {failures.map(event => (
        <div className="app-result app-team-error" role="alert" key={`${event.timestamp}-error`}>
          {event.lastStatus}: {event.errorMessage}
        </div>
      ))}

      <h4 className="app-job-report-heading">Status callbacks</h4>
      {callbacks.length ? (
        callbacks.map(event => <CallbackRow event={event} key={`${event.timestamp}-${event.eventId}`} />)
      ) : (
        <p className="app-job-empty">None recorded. Jobs older than the callback log, and teams without a callback URL, have none.</p>
      )}

      <h4 className="app-job-report-heading">Lifecycle</h4>
      {statuses.length ? (
        <ul className="app-job-lifecycle">
          {statuses.map(event => (
            <li key={`${event.timestamp}-${event.eventId}`}>
              <code>{event.lastStatus}</code> {event.timestamp}
            </li>
          ))}
        </ul>
      ) : (
        <p className="app-job-empty">No status events.</p>
      )}

      {protocolSteps.map((step, index) => (
        <StepRow
          key={`protocol-${index}`}
          status={step.exitCode === 0 ? 'ok' : 'error'}
          label={`Agent run ${index + 1}${step.duration !== undefined ? ` · ${Math.round(step.duration / 1000)} s` : ''}`}
          exitCode={step.exitCode}
          output={[step.stderr, step.stdout].filter(Boolean).join('\n').slice(-PROTOCOL_TAIL_LENGTH)}
        />
      ))}
    </div>
  );
}

/** Read-only debugging detail of one job: the steps the agent ran and what the platform recorded. */
export function JobDebugSections({
  fesConfig,
  tokens,
  requestId,
  defaultOpen,
}: {
  fesConfig: FesConfig;
  tokens: AuthTokens;
  requestId: string;
  defaultOpen: boolean;
}) {
  return (
    <>
      <Section title="Run steps" meta="commands and output" defaultOpen={defaultOpen}>
        {isOpen => <RunStepsLoader fesConfig={fesConfig} tokens={tokens} requestId={requestId} isOpen={isOpen} />}
      </Section>
      <Section title="Report" meta="failures, callbacks, lifecycle" defaultOpen={defaultOpen}>
        {isOpen => <ReportLoader fesConfig={fesConfig} tokens={tokens} requestId={requestId} isOpen={isOpen} />}
      </Section>
    </>
  );
}

type LoaderProps = { fesConfig: FesConfig; tokens: AuthTokens; requestId: string; isOpen: boolean };

function RunStepsLoader({ fesConfig, tokens, requestId, isOpen }: LoaderProps) {
  const { data, error, loading } = useLazyLoad(isOpen, async () => {
    const response = await getChatMessages(fesConfig, tokens, requestId, TRANSCRIPT_LIMIT);
    const items = response.items || [];

    return { messages: buildMessages(items), commands: readCommands(items) };
  });

  if (error) {
    return <p className="app-job-empty">{error}</p>;
  }

  return loading || !data ? <Loading /> : <RunSteps messages={data.messages} commands={data.commands} />;
}

function ReportLoader({ fesConfig, tokens, requestId, isOpen }: LoaderProps) {
  const { data, error, loading } = useLazyLoad(isOpen, () => getJobReport(fesConfig, tokens, requestId));

  if (error) {
    return <p className="app-job-empty">{error}</p>;
  }

  return loading || !data ? <Loading /> : <ReportBody report={data} />;
}
